import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  ShieldCheck,
  Mail,
  Check,
  Eye,
  EyeOff,
  Users,
  Clock,
  ImagePlus,
  Building2,
} from "lucide-react";
import { COMPANIES, CompanyLogo, companyName } from "../../companies";
import Cropper from "react-easy-crop";
import {
  adminListUsers,
  adminCreateUser,
  adminUpdateUser,
  adminDeleteUser,
  adminUploadImage,
  adminUpdateCompanyLogo,
} from "../../api";

const absUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}${path}`;

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (e) => reject(e));
    img.setAttribute("crossOrigin", "anonymous");
    img.src = url;
  });

// Produce a square JPEG blob from the chosen crop area.
const getCroppedBlob = async (imageSrc, area, size = 400) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, size, size);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.9));
};

const CropModal = ({ src, busy, onCancel, onApply }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={busy ? undefined : onCancel} />
      <div
        data-testid="crop-modal"
        style={{ fontFamily: "Urbanist, sans-serif" }}
        className="relative z-10 w-full max-w-[440px] rounded-[24px] border border-[#EAE3DB] bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-[#141019]"
      >
        <h3 className="mb-4 text-lg font-bold text-[#1A1815] dark:text-white">
          Position your photo
        </h3>
        <div className="relative h-64 w-full overflow-hidden rounded-2xl bg-black">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_, px) => setAreaPixels(px)}
          />
        </div>
        <div className="mt-4 flex items-center gap-3">
          <span className="text-xs font-semibold text-[#8B8680] dark:text-white/45">Zoom</span>
          <input
            data-testid="crop-zoom"
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-[#E7E0D8] accent-[#1A1815] dark:bg-white/15 dark:accent-white"
          />
        </div>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={busy}
            className="rounded-xl px-5 py-3 text-sm font-semibold text-[#8B8680] transition-colors hover:text-[#1A1815] disabled:opacity-50 dark:text-white/50 dark:hover:text-white"
          >
            Cancel
          </button>
          <button
            data-testid="crop-apply"
            onClick={() => areaPixels && onApply(areaPixels)}
            disabled={busy || !areaPixels}
            className="flex items-center justify-center gap-2 rounded-xl bg-[#1A1815] px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60 dark:bg-white dark:text-black"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
};

const EMPTY_FORM = {
  name: "",
  email: "",
  password: "",
  role: "",
  companies: [],
  twofa: false,
  avatar: "",
};

const DAY_MS = 24 * 60 * 60 * 1000;

// Returns { label, stale } — stale = never signed in or inactive 30+ days.
const lastLogin = (iso) => {
  if (!iso) return { label: "Never signed in", stale: true };
  const then = new Date(iso);
  if (isNaN(then)) return { label: "Never signed in", stale: true };
  const diff = Date.now() - then.getTime();
  const days = Math.floor(diff / DAY_MS);
  let label;
  if (diff < 60 * 1000) label = "Just now";
  else if (diff < 60 * 60 * 1000) {
    const m = Math.floor(diff / 60000);
    label = `${m} min${m > 1 ? "s" : ""} ago`;
  } else if (days < 1) {
    const h = Math.floor(diff / (60 * 60 * 1000));
    label = `${h} hour${h > 1 ? "s" : ""} ago`;
  } else if (days === 1) label = "Yesterday";
  else if (days < 30) label = `${days} days ago`;
  else {
    label = then.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  }
  return { label, stale: days >= 30 };
};

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-[#8B8680] dark:text-white/45">
      {label}
    </span>
    {children}
    {hint && <span className="mt-1 block text-xs text-[#B0A99F] dark:text-white/35">{hint}</span>}
  </label>
);

const inputCls =
  "w-full rounded-xl border border-[#E7E0D8] bg-white px-4 py-3 text-sm font-medium text-[#1A1815] outline-none transition-colors placeholder:text-[#B0A99F] focus:border-[#1A1815] dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:placeholder:text-white/35 dark:focus:border-white/40";

const UserDialog = ({ open, editing, onClose, onSaved, onAction }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const avatarInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setError("");
      setShowPass(false);
      if (editing) {
        setForm({
          name: editing.name || "",
          email: editing.email || "",
          password: "",
          role: editing.role || "",
          companies: editing.companies || [],
          twofa: !!editing.twofa,
          avatar: editing.avatar || "",
        });
      } else {
        setForm(EMPTY_FORM);
      }
    }
  }, [open, editing]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCompany = (key) =>
    setForm((f) => ({
      ...f,
      companies: f.companies.includes(key)
        ? f.companies.filter((c) => c !== key)
        : [...f.companies, key],
    }));

  const handleAvatarFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(f.type)) {
      setError("Please choose a JPG, PNG or WebP image.");
      return;
    }
    setError("");
    setCropSrc(URL.createObjectURL(f));
  };

  const applyCrop = async (areaPixels) => {
    setAvatarUploading(true);
    try {
      const blob = await getCroppedBlob(cropSrc, areaPixels);
      const file = new File([blob], "avatar.jpg", { type: "image/jpeg" });
      const res = await adminUploadImage(file);
      set("avatar", absUrl(res.url));
      if (cropSrc) URL.revokeObjectURL(cropSrc);
      setCropSrc(null);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not upload the photo. Please try again.");
    } finally {
      setAvatarUploading(false);
    }
  };

  const cancelCrop = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };

  const handleSave = async () => {
    setError("");
    if (!form.name.trim()) return setError("Please enter a name.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()))
      return setError("Please enter a valid email address.");
    if (!editing && form.password.length < 6)
      return setError("Password must be at least 6 characters.");
    if (editing && form.password && form.password.length < 6)
      return setError("Password must be at least 6 characters.");
    if (form.companies.length === 0) return setError("Select at least one company.");

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role.trim() || "Staff",
        companies: form.companies,
        twofa: form.twofa,
        avatar: form.avatar.trim(),
      };
      if (editing) {
        if (form.password) payload.password = form.password;
        await adminUpdateUser(editing.id, payload);
        onAction(`Updated ${payload.name}`);
      } else {
        payload.password = form.password;
        await adminCreateUser(payload);
        onAction(`Added ${payload.name}`);
      }
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not save the user. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        data-testid="user-dialog-overlay"
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        data-testid="user-dialog"
        style={{ fontFamily: "Urbanist, sans-serif" }}
        className="relative z-10 max-h-[90vh] w-full max-w-[560px] overflow-y-auto rounded-[26px] border border-[#EAE3DB] bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-[#141019]"
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-xl font-bold tracking-tight text-[#1A1815] dark:text-white">
            {editing ? "Edit user" : "Add a user"}
          </h2>
          <button
            data-testid="user-dialog-close"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F0EBE5] text-[#1A1815] transition-colors hover:bg-[#E7E0D8] dark:bg-white/5 dark:text-white/70 dark:hover:bg-white/10"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <img
              src={form.avatar || "https://i.pravatar.cc/80?img=32"}
              alt="avatar preview"
              data-testid="avatar-preview"
              className="h-16 w-16 shrink-0 rounded-full object-cover ring-1 ring-[#E7E0D8] dark:ring-white/10"
            />
            <div className="min-w-0 flex-1">
              <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.1em] text-[#8B8680] dark:text-white/45">
                Profile photo
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="avatar-upload-btn"
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={avatarUploading}
                  className="flex shrink-0 items-center gap-1.5 rounded-xl bg-[#1A1815] px-3.5 py-2.5 text-xs font-semibold text-white transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60 dark:bg-white dark:text-black"
                >
                  {avatarUploading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <ImagePlus className="h-3.5 w-3.5" strokeWidth={2} />
                  )}
                  Upload
                </button>
                <input
                  data-testid="avatar-url-input"
                  className={inputCls + " flex-1"}
                  value={form.avatar}
                  onChange={(e) => set("avatar", e.target.value)}
                  placeholder="…or paste an image URL"
                />
              </div>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={handleAvatarFile}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Full name">
              <input
                data-testid="user-form-name"
                className={inputCls}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="e.g. Fatou Ceesay"
              />
            </Field>
            <Field label="Role / Title">
              <input
                data-testid="user-form-role"
                className={inputCls}
                value={form.role}
                onChange={(e) => set("role", e.target.value)}
                placeholder="e.g. Finance Officer"
              />
            </Field>
          </div>

          <Field label="Email address">
            <input
              data-testid="user-form-email"
              className={inputCls}
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="name@company.gm"
            />
          </Field>

          <Field
            label={editing ? "Reset password" : "Initial password"}
            hint={editing ? "Leave blank to keep the current password." : "Share this with the user so they can sign in."}
          >
            <div className="relative">
              <input
                data-testid="user-form-password"
                className={inputCls + " pr-12"}
                type={showPass ? "text" : "password"}
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                placeholder={editing ? "••••••••" : "At least 6 characters"}
              />
              <button
                type="button"
                onClick={() => setShowPass((s) => !s)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#8B8680] transition-colors hover:text-[#1A1815] dark:text-white/40 dark:hover:text-white/80"
              >
                {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </Field>

          <Field label="Company access">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {COMPANIES.map((c) => {
                const on = form.companies.includes(c.key);
                return (
                  <button
                    key={c.key}
                    type="button"
                    data-testid={`user-form-company-${c.key}`}
                    onClick={() => toggleCompany(c.key)}
                    className={`flex items-center gap-2 rounded-xl border-2 px-2.5 py-2 text-left transition-all duration-150 ${
                      on
                        ? "border-[#1A1815] bg-[#F6EFE8] dark:border-[#7AFFA1] dark:bg-white/[0.06]"
                        : "border-transparent bg-[#F6EFE8] hover:border-[#E7E0D8] dark:bg-white/[0.03] dark:hover:border-white/15"
                    }`}
                  >
                    <CompanyLogo company={c} className="h-7 w-7 rounded-md" />
                    <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-[#1A1815] dark:text-white/85">
                      {c.name}
                    </span>
                    {on && <Check className="h-3.5 w-3.5 shrink-0 text-[#1A1815] dark:text-[#7AFFA1]" strokeWidth={3} />}
                  </button>
                );
              })}
            </div>
          </Field>

          <button
            type="button"
            data-testid="user-form-twofa"
            onClick={() => set("twofa", !form.twofa)}
            className="flex w-full items-center justify-between rounded-xl bg-[#F6EFE8] px-4 py-3 text-left dark:bg-white/[0.04]"
          >
            <span className="flex items-center gap-2.5">
              <Mail className="h-4 w-4 text-[#1A1815] dark:text-white/70" strokeWidth={1.8} />
              <span className="text-sm font-semibold text-[#1A1815] dark:text-white">
                Email 2FA at login
              </span>
            </span>
            <span
              className={`relative h-6 w-11 rounded-full transition-colors ${
                form.twofa ? "bg-[#2FBF71]" : "bg-[#D6CEC4] dark:bg-white/15"
              }`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                  form.twofa ? "left-[22px]" : "left-0.5"
                }`}
              />
            </span>
          </button>

          {error && (
            <p data-testid="user-form-error" className="text-sm font-medium text-[#B42318] dark:text-[#FF8A80]">
              {error}
            </p>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-xl px-5 py-3 text-sm font-semibold text-[#8B8680] transition-colors hover:text-[#1A1815] dark:text-white/50 dark:hover:text-white"
          >
            Cancel
          </button>
          <button
            data-testid="user-form-save"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center justify-center gap-2 rounded-xl bg-[#1A1815] px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60 dark:bg-white dark:text-black"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Create user"}
          </button>
        </div>
      </div>

      {cropSrc && (
        <CropModal
          src={cropSrc}
          busy={avatarUploading}
          onCancel={cancelCrop}
          onApply={applyCrop}
        />
      )}
    </div>
  );
};

const CompaniesPanel = ({ onAction, refreshLogos }) => {
  const [busyKey, setBusyKey] = useState(null);
  const inputRefs = useRef({});

  const handleFile = async (key, e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusyKey(key);
    try {
      const res = await adminUploadImage(f);
      await adminUpdateCompanyLogo(key, absUrl(res.url));
      if (refreshLogos) await refreshLogos();
      onAction(`Updated ${companyName(key)} logo`);
    } catch (err) {
      const d = err.response?.data?.detail;
      onAction(typeof d === "string" ? d : "Could not update the logo.");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div
      data-testid="companies-panel"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
    >
      {COMPANIES.map((c) => (
        <div
          key={c.key}
          data-testid={`company-card-${c.key}`}
          className="flex items-center gap-3 rounded-2xl border border-[#EAE3DB] bg-white p-3.5 dark:border-white/10 dark:bg-white/[0.03]"
        >
          <CompanyLogo company={c} className="h-14 w-14 rounded-xl" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-[#1A1815] dark:text-white">
              {c.name}
            </p>
            <button
              type="button"
              data-testid={`company-logo-upload-${c.key}`}
              onClick={() => inputRefs.current[c.key]?.click()}
              disabled={busyKey === c.key}
              className="mt-1.5 flex items-center gap-1.5 rounded-lg bg-[#F0EBE5] px-3 py-1.5 text-xs font-semibold text-[#1A1815] transition-colors hover:bg-[#E7E0D8] disabled:opacity-60 dark:bg-white/5 dark:text-white/80 dark:hover:bg-white/10"
            >
              {busyKey === c.key ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ImagePlus className="h-3.5 w-3.5" strokeWidth={2} />
              )}
              Change logo
            </button>
            <input
              ref={(el) => (inputRefs.current[c.key] = el)}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => handleFile(c.key, e)}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const ManageUsers = ({ dark, user, onAction, refreshLogos }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminListUsers()
      .then(setUsers)
      .catch(() => onAction("Failed to load users"))
      .finally(() => setLoading(false));
  }, [onAction]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminDeleteUser(deleteTarget.id);
      onAction(`Removed ${deleteTarget.name}`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      onAction(typeof d === "string" ? d : "Could not delete the user.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="px-6 pt-6 md:px-10 md:pt-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            data-testid="manage-users-back"
            onClick={() => navigate("/")}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F0EBE5] text-[#1A1815] transition-colors hover:bg-[#E7E0D8] dark:border dark:border-white/10 dark:bg-white/5 dark:text-white/70 dark:hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.8} />
          </button>
          <div>
            <h1 className="flex items-center gap-2.5 text-2xl font-bold tracking-tight text-[#1A1815] dark:text-white">
              <Users className="h-6 w-6" strokeWidth={1.8} />
              Manage Users
            </h1>
            <p className="mt-0.5 text-sm text-[#8B8680] dark:text-white/45">
              Add teammates, set company access, control login security, and brand your companies.
            </p>
          </div>
        </div>
        {tab === "users" && (
          <button
            data-testid="manage-users-add-btn"
            onClick={openAdd}
            className="flex items-center gap-2 rounded-full bg-[#1A1815] px-5 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.03] active:scale-95 dark:bg-white dark:text-black"
          >
            <Plus className="h-4 w-4" strokeWidth={2.2} />
            Add user
          </button>
        )}
      </header>

      <div className="mt-7 flex gap-2">
        <button
          data-testid="tab-users"
          onClick={() => setTab("users")}
          className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
            tab === "users"
              ? "bg-[#1A1815] text-white dark:bg-white dark:text-black"
              : "bg-[#F0EBE5] text-[#1A1815] hover:bg-[#E7E0D8] dark:bg-white/5 dark:text-white/70 dark:hover:bg-white/10"
          }`}
        >
          <Users className="h-4 w-4" strokeWidth={1.9} />
          Users
        </button>
        <button
          data-testid="tab-companies"
          onClick={() => setTab("companies")}
          className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
            tab === "companies"
              ? "bg-[#1A1815] text-white dark:bg-white dark:text-black"
              : "bg-[#F0EBE5] text-[#1A1815] hover:bg-[#E7E0D8] dark:bg-white/5 dark:text-white/70 dark:hover:bg-white/10"
          }`}
        >
          <Building2 className="h-4 w-4" strokeWidth={1.9} />
          Company logos
        </button>
      </div>

      {tab === "companies" ? (
        <div className="mt-6">
          <CompaniesPanel onAction={onAction} refreshLogos={refreshLogos} />
        </div>
      ) : (
      <div className="mt-6">
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#8B8680] dark:text-white/50" />
          </div>
        ) : (
          <div
            data-testid="manage-users-list"
            className="overflow-hidden rounded-[22px] border border-[#EAE3DB] bg-white dark:border-white/10 dark:bg-white/[0.03]"
          >
            {users.map((u, i) => {
              const isSelf = u.id === user?.id;
              return (
                <div
                  key={u.id}
                  data-testid={`user-row-${u.id}`}
                  className={`flex flex-wrap items-center gap-4 px-5 py-4 ${
                    i !== users.length - 1 ? "border-b border-[#EAE3DB] dark:border-white/10" : ""
                  }`}
                >
                  <img
                    src={u.avatar || "https://i.pravatar.cc/80?img=32"}
                    alt={u.name}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-base font-bold text-[#1A1815] dark:text-white">
                        {u.name}
                      </span>
                      {u.is_admin && (
                        <span className="flex items-center gap-1 rounded-full bg-[#1A1815] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white dark:bg-[#7AFFA1] dark:text-black">
                          <ShieldCheck className="h-3 w-3" strokeWidth={2.4} /> Admin
                        </span>
                      )}
                      {u.twofa && (
                        <span className="flex items-center gap-1 rounded-full bg-[#F0EBE5] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#1A1815] dark:bg-white/10 dark:text-white/80">
                          <Mail className="h-3 w-3" strokeWidth={2.2} /> 2FA
                        </span>
                      )}
                    </div>
                    <p className="truncate text-sm text-[#8B8680] dark:text-white/45">
                      {u.email} · {u.role}
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {u.companies.slice(0, 5).map((c) => (
                        <span
                          key={c}
                          className="rounded-md bg-[#F6EFE8] px-2 py-0.5 text-[11px] font-semibold text-[#5c5852] dark:bg-white/[0.06] dark:text-white/55"
                        >
                          {companyName(c)}
                        </span>
                      ))}
                      {u.companies.length > 5 && (
                        <span className="rounded-md bg-[#F6EFE8] px-2 py-0.5 text-[11px] font-semibold text-[#5c5852] dark:bg-white/[0.06] dark:text-white/55">
                          +{u.companies.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    data-testid={`user-lastlogin-${u.id}`}
                    className="flex min-w-[130px] flex-col items-start gap-0.5 sm:items-end"
                  >
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#B0A99F] dark:text-white/35">
                      Last sign-in
                    </span>
                    <span
                      className={`flex items-center gap-1.5 text-sm font-semibold ${
                        lastLogin(u.last_login_at).stale
                          ? "text-[#C77700] dark:text-[#FFB454]"
                          : "text-[#1A1815] dark:text-white/80"
                      }`}
                    >
                      <Clock className="h-3.5 w-3.5" strokeWidth={2} />
                      {lastLogin(u.last_login_at).label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`user-edit-${u.id}`}
                      onClick={() => openEdit(u)}
                      title="Edit"
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F0EBE5] text-[#1A1815] transition-colors hover:bg-[#E7E0D8] dark:bg-white/5 dark:text-white/70 dark:hover:bg-white/10"
                    >
                      <Pencil className="h-4 w-4" strokeWidth={1.8} />
                    </button>
                    <button
                      data-testid={`user-delete-${u.id}`}
                      onClick={() => setDeleteTarget(u)}
                      disabled={isSelf}
                      title={isSelf ? "You can't delete your own admin account" : "Delete"}
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F0EBE5] text-[#B42318] transition-colors hover:bg-[#F2C0C0]/40 disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-[#F0EBE5] dark:bg-white/5 dark:text-[#FF8A80] dark:hover:bg-white/10"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      <UserDialog
        open={dialogOpen}
        editing={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={() => {
          setDialogOpen(false);
          load();
        }}
        onAction={onAction}
      />

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setDeleteTarget(null)}
          />
          <div
            data-testid="delete-confirm-dialog"
            style={{ fontFamily: "Urbanist, sans-serif" }}
            className="relative z-10 w-full max-w-[420px] rounded-[24px] border border-[#EAE3DB] bg-white p-6 text-center shadow-2xl dark:border-white/10 dark:bg-[#141019]"
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#F2C0C0]/40 dark:bg-[#FF8A80]/10">
              <Trash2 className="h-6 w-6 text-[#B42318] dark:text-[#FF8A80]" strokeWidth={1.8} />
            </div>
            <h3 className="text-lg font-bold text-[#1A1815] dark:text-white">
              Remove {deleteTarget.name}?
            </h3>
            <p className="mt-1.5 text-sm text-[#8B8680] dark:text-white/50">
              This permanently deletes their account and sign-in access. This cannot be undone.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 rounded-xl bg-[#F0EBE5] py-3 text-sm font-semibold text-[#1A1815] transition-colors hover:bg-[#E7E0D8] dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                data-testid="delete-confirm-btn"
                onClick={confirmDelete}
                disabled={deleting}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#B42318] py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUsers;
